import axios, { type AxiosRequestConfig } from 'axios';
import {
  STORAGE_KEYS,
  createRefreshRejectedHandler,
  AuthRefreshError,
  type RetriableRequestConfig,
} from '@vvf/shared';
import {
  NATIVE_EVENT,
  type NativeTokensSyncedDetail,
  type NativeTokenRefreshFailedDetail,
} from '@vvf/shared/bridge';
import {
  isInNativeWrapper,
  requestNativeTokenRefresh,
} from './nativeBridge';

const ACCESS_TOKEN_KEY = STORAGE_KEYS.ACCESS_TOKEN;
const REFRESH_TOKEN_KEY = STORAGE_KEYS.REFRESH_TOKEN;
const USER_KEY = STORAGE_KEYS.USER;

/**
 * Audit #2 + Item #6 (anti-XSS): in browser puro (no wrapper) usiamo refresh
 * token su cookie httpOnly invece di localStorage, e l'ACCESS token vive solo
 * in una variabile di MODULO (memoria), MAI in localStorage. Cosi' uno script
 * XSS non puo' leggere ne' il refresh (httpOnly) ne' l'access (non in storage).
 *
 * In WRAPPER mode (RN WebView) i token restano in localStorage: il nativo li
 * legge per rehydrate al cold-start e li sincronizza col SecureStore. Non
 * cambiamo la dualita': isInNativeWrapper() decide il regime.
 *
 * Server-side il cookie e' Strict/Secure/HttpOnly su path /api/auth.
 *
 * LAZY a runtime: `isInNativeWrapper()` legge `window.IS_NATIVE_WRAPPER`, che
 * il wrapper potrebbe non aver ancora settato al momento dell'import di questo
 * modulo. Valutare il regime a import-time (vecchia costante) poteva quindi
 * classificare erroneamente come cookie-mode un avvio in wrapper. Per questo
 * `isCookieAuth()` e' una funzione, chiamata ad ogni uso.
 */
function isCookieAuth(): boolean {
  return !isInNativeWrapper();
}

/**
 * Item #6: access token in memoria (solo cookie mode). In wrapper mode questa
 * variabile resta null e usiamo sempre localStorage (il nativo ne ha bisogno).
 */
let memoryAccessToken: string | null = null;

/** Legge l'access token dal regime corretto (memoria in cookie mode, storage in wrapper). */
export function getAccessToken(): string | null {
  if (isCookieAuth()) return memoryAccessToken;
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

// NB: withCredentials e l'header X-Auth-Mode NON sono decisi qui (import-time):
// il regime (cookie vs wrapper) e' valutato a runtime nell'interceptor di
// request, perche' window.IS_NATIVE_WRAPPER potrebbe non essere ancora settato
// quando questo modulo viene importato.
export const api = axios.create({
  baseURL: '/api',
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  // Regime auth valutato a runtime (lazy): in cookie mode includiamo il cookie
  // httpOnly (withCredentials) e segnaliamo il mode al server con X-Auth-Mode.
  config.withCredentials = isCookieAuth();
  if (isCookieAuth()) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)['X-Auth-Mode'] = 'cookie';
  }
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  // Idempotency-Key automatica per metodi mutanti (POST/PUT/PATCH/DELETE).
  // Il server (IdempotencyMiddleware) cache-a la response 24h per la tuple
  // (userId, method, path, idemKey) — un retry network dell'identica chiamata
  // non duplica side-effect (FCM doppi, response doppi, ecc.).
  // Il client puo' sovrascrivere passando un Idempotency-Key custom (es.
  // identificativo logico dell'operazione per dedup applicativa).
  const method = (config.method ?? 'get').toUpperCase();
  if (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE') {
    config.headers = config.headers ?? {};
    const headers = config.headers as Record<string, string>;
    if (!headers['Idempotency-Key'] && !headers['idempotency-key']) {
      headers['Idempotency-Key'] = generateIdempotencyKey();
    }
  }
  return config;
});

function generateIdempotencyKey(): string {
  // UUID v4 best-effort senza dipendenze: crypto.randomUUID se disponibile,
  // fallback timestamp + random.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

// Refresh token flow: su 401, prova a refreshare una volta sola, poi retry la request originale.
let refreshPromise: Promise<string> | null = null;

/**
 * Callback registrato da AuthProvider per propagare gli aggiornamenti
 * di `user` ricevuti dal backend (login response, refresh response).
 * Senza, api.ts scriveva direttamente in localStorage e i useAuth()
 * consumer non si re-renderizzavano (audit techlead #3).
 */
type UserUpdateCallback = (user: Record<string, unknown>) => void;
let onUserUpdated: UserUpdateCallback | null = null;
export function registerUserUpdateCallback(cb: UserUpdateCallback | null): void {
  onUserUpdated = cb;
}

function applyUserUpdate(user: Record<string, unknown> | undefined): void {
  if (!user) return;
  // Propaga user update via AuthContext (audit techlead #3) invece di
  // scrivere direttamente in localStorage, cosi' tutti i useAuth()
  // consumer si re-renderizzano. Fallback localStorage solo se nessun
  // callback registrato (es. inizializzazione precoce).
  if (onUserUpdated) onUserUpdated(user);
  else localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/**
 * Item #1 (single-owner refresh ibrido): in WRAPPER mode l'OWNER UNICO del
 * refresh e' il NATIVO. La scelta e' obbligata dal fatto che il task GPS
 * background (TaskManager) gira con la WebView potenzialmente distrutta:
 * solo il nativo puo' rinnovare in autonomia in quel contesto. Per evitare
 * che PWA e nativo chiamino /auth/refresh sullo stesso refresh token che
 * ruota (race -> 401/logout), la PWA NON chiama mai /auth/refresh in wrapper:
 * chiede al nativo via bridge tipizzato (auth:request-refresh) e attende che
 * il nativo sincronizzi il nuovo access token in localStorage, poi lo rilegge.
 * Round-trip nativo->PWA gia' esistente: registerTokenRefreshSync nel wrapper
 * scrive vvf.access_token/refresh_token in localStorage dopo ogni refresh.
 */
async function refreshViaNative(): Promise<string> {
  // Item #5: meccanismo a EVENTO al posto del busy-poll su localStorage.
  // Il nativo, dopo aver sincronizzato i token (registerTokenRefreshSync ->
  // injectJavaScript), dispatcha NATIVE_EVENT.TokensSynced sul window della
  // PWA. Qui attendiamo quell'evento. Manteniamo un timeout di sicurezza 7s
  // come fallback: allo scadere -> throw -> l'interceptor fa logout/redirect.
  // Comportamento finale invariato (la PWA ottiene il nuovo access), ma senza
  // polling. Il listener e' registrato PRIMA di requestNativeTokenRefresh per
  // non perdere l'evento se il nativo risponde velocemente.
  const before = localStorage.getItem(ACCESS_TOKEN_KEY);

  return new Promise<string>((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = (): void => {
      window.removeEventListener(NATIVE_EVENT.TokensSynced, onSynced);
      window.removeEventListener(NATIVE_EVENT.TokenRefreshFailed, onFailed);
      if (timer !== null) clearTimeout(timer);
    };

    const onSynced = (e: Event): void => {
      if (settled) return;
      // L'access token arriva nel detail dell'evento; in fallback rileggiamo
      // da localStorage (gia' scritto dal nativo prima del dispatch).
      const detail = (e as CustomEvent<NativeTokensSyncedDetail>).detail;
      const token = detail?.accessToken ?? localStorage.getItem(ACCESS_TOKEN_KEY);
      if (!token) return; // evento spurio senza token: continua ad attendere
      settled = true;
      cleanup();
      resolve(token);
    };

    // Il nativo segnala il fallimento del refresh col kind: 'auth' (sessione
    // invalida -> login) o 'network' (transitorio -> NON sloggare). Rigettiamo
    // con un AuthRefreshError tipizzato cosi' l'interceptor decide di conseguenza.
    const onFailed = (e: Event): void => {
      if (settled) return;
      const detail = (e as CustomEvent<NativeTokenRefreshFailedDetail>).detail;
      const kind = detail?.kind ?? 'network';
      settled = true;
      cleanup();
      reject(new AuthRefreshError(kind, 'Refresh nativo fallito'));
    };

    window.addEventListener(NATIVE_EVENT.TokensSynced, onSynced);
    window.addEventListener(NATIVE_EVENT.TokenRefreshFailed, onFailed);

    const ok = requestNativeTokenRefresh();
    if (!ok) {
      settled = true;
      cleanup();
      // Bridge non disponibile = condizione transitoria (es. WebView non ancora
      // pronta): trattiamo come 'network', non sloggare.
      reject(new AuthRefreshError('network', 'Native refresh non disponibile'));
      return;
    }

    // Timeout di sicurezza (invariato a 7s). Lo scadere e' una condizione
    // TRANSITORIA ('network'): NON deve sloggare l'utente.
    timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      // Best-effort: se nel frattempo il token e' comunque cambiato (evento
      // perso ma localStorage aggiornato) lo accettiamo, altrimenti network error.
      const current = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (current && current !== before) resolve(current);
      else reject(new AuthRefreshError('network', 'Timeout refresh nativo'));
    }, 7_000);
  });
}

async function refreshAccessToken(): Promise<string> {
  // Cookie mode (audit #2 + item #6): il refresh token e' nel cookie httpOnly,
  // il body POST e' vuoto. Il server lo legge da Request.Cookies. Il nuovo
  // access token resta SOLO in memoria (memoryAccessToken), non in localStorage.
  if (isCookieAuth()) {
    // Classificazione errori: SOLO un 401/403 del server e' un fallimento di
    // autenticazione DEFINITIVO ('auth'). Tutto il resto (nessuna response,
    // timeout, 5xx, errore rete) e' TRANSITORIO ('network') e NON deve sloggare.
    let res;
    try {
      res = await axios.post('/api/auth/refresh', {}, {
        withCredentials: true,
        headers: { 'X-Auth-Mode': 'cookie' },
      });
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 401 || status === 403) {
        throw new AuthRefreshError('auth', 'Refresh rifiutato dal server');
      }
      throw new AuthRefreshError('network', 'Refresh fallito per errore transitorio');
    }
    const newAccess: string = res.data.accessToken;
    memoryAccessToken = newAccess;
    applyUserUpdate(res.data.user);
    return newAccess;
  }

  // Wrapper mode (item #1): la PWA non e' owner del refresh. Delega al nativo.
  const newAccess = await refreshViaNative();
  return newAccess;
}

/**
 * Refresh coalescato sul singleton di modulo `refreshPromise`. Condiviso tra
 * l'interceptor (factory item #4) e bootstrapAuth(): cosi' un silent-refresh
 * al boot e un 401 concorrente NON producono due chiamate /auth/refresh sullo
 * stesso refresh token che ruota (comportamento preesistente preservato).
 */
async function coalescedRefresh(): Promise<string> {
  refreshPromise ??= refreshAccessToken();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

function logoutAndRedirect(): void {
  memoryAccessToken = null;
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/hydrants-public')) {
    window.location.href = '/login';
  }
}

api.interceptors.response.use(
  (response) => {
    // Hardening: se il proxy non e' configurato per /api e nginx serve la SPA,
    // riceviamo una stringa HTML invece del JSON atteso. Rifiutiamo: meglio un
    // errore esplicito che cachare HTML come "dati" (porta a *.map is not a fn).
    const ct = response.headers?.['content-type'] ?? response.headers?.['Content-Type'];
    if (typeof response.data === 'string' && response.data.startsWith('<!doctype')) {
      return Promise.reject(new Error(`Risposta HTML inattesa da ${response.config.url}. Routing /api errato?`));
    }
    if (typeof ct === 'string' && ct.includes('text/html')) {
      return Promise.reject(new Error(`Risposta non-JSON (${ct}) da ${response.config.url}.`));
    }
    return response;
  },
  // Item #4: handler 401 estratto nella factory condivisa @vvf/shared.
  // Regime web: refresh cookie/memoria (o delega nativa in wrapper) +
  // logoutAndRedirect su fallimento. Coalescing via coalescedRefresh
  // (singleton di modulo condiviso con bootstrapAuth).
  createRefreshRejectedHandler<AxiosRequestConfig & RetriableRequestConfig, unknown>({
    refreshFn: coalescedRefresh,
    setAuthHeader: (config, token) => {
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    },
    runner: { request: (config) => api.request(config) },
    onLogout: logoutAndRedirect,
    // logoutAndRedirect scatta SOLO su fallimento auth definitivo (401/403
    // server, oppure 'auth' propagato dal nativo). Su errore transitorio
    // (rete/timeout/5xx) la sessione resta: niente redirect a /login.
    isAuthError: (e) => e instanceof AuthRefreshError && e.kind === 'auth',
  }),
);

/**
 * Imposta l'access token nel regime corretto.
 *  - cookie mode (browser): variabile di modulo (memoria), anti-XSS.
 *  - wrapper mode (RN): localStorage, cosi' il nativo lo rehydrata/sincronizza.
 */
export function setAccessToken(token: string | null): void {
  if (isCookieAuth()) {
    memoryAccessToken = token;
    return;
  }
  if (token) {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
  }
}

/**
 * Item #6: silent-refresh al boot in COOKIE mode. Al reload pagina l'access
 * token in memoria e' perso, ma il refresh token e' nel cookie httpOnly:
 * proviamo un refresh silenzioso per riottenere l'access e l'utente loggato.
 * Ritorna true se la sessione e' stata ripristinata (utente loggato).
 *
 * In WRAPPER mode fa un refresh PROATTIVO al boot, ma con la regola anti-logout:
 * un errore TRANSITORIO (rete/timeout) NON deve buttare l'utente al login se
 * c'e' una sessione (token in localStorage) da riprovare piu' tardi.
 */
export async function bootstrapAuth(): Promise<boolean> {
  if (isCookieAuth()) {
    // Browser puro: silent-refresh via cookie httpOnly. Non deve mai lanciare:
    // success -> loggato; qualunque errore (auth o network) -> non loggato qui,
    // l'utente vedra' /login ma la sessione cookie eventualmente resta valida.
    try {
      // Usa lo stesso singleton coalescato dell'interceptor (item #4): un 401
      // concorrente al boot non innesca un secondo /auth/refresh.
      const token = await coalescedRefresh();
      return Boolean(token);
    } catch {
      return false;
    }
  }

  // Wrapper: refresh PROATTIVO al boot delegato al nativo. Se non c'e' alcun
  // token salvato non c'e' sessione da ripristinare. Altrimenti tentiamo il
  // refresh: solo un fallimento 'auth' definitivo manda al login; un errore di
  // rete NON slogga (restiamo in sessione e si ritentera').
  const hasAccess = !!localStorage.getItem(ACCESS_TOKEN_KEY);
  const hasRefresh = !!localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!hasAccess && !hasRefresh) return false;
  try {
    await coalescedRefresh();
    return true;
  } catch (e) {
    if (e instanceof AuthRefreshError && e.kind === 'auth') return false; // sessione invalida -> login
    return hasAccess || hasRefresh; // errore di rete: NON sloggare, resta in sessione
  }
}

/**
 * Logout con revoca lato server (fix minore review): POST /auth/logout invalida
 * il refresh token nel DB e, in cookie mode, cancella il cookie httpOnly
 * (ClearRefreshCookie). Best-effort: anche se la rete fallisce, puliamo comunque
 * lo stato locale. La rimozione di USER_KEY (che notifica gli altri tab via
 * storage event) la fa il chiamante con AuthContext.setUser(null).
 */
export async function serverLogout(): Promise<void> {
  try { await api.post('/auth/logout'); } catch { /* best-effort */ }
  memoryAccessToken = null;
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}
